// The browser's copy of the file-writing rules.
//
// The source of truth is `file-write.ts` in common-lib, which the API route (`pages/api/write-file.ts`)
// and the native-data bridge both import directly. This file is a hand-duplicated copy of the parts the
// CLIENT needs, and the duplication is deliberate — the same call `lib/file-record.ts` makes, for the same
// reason, now demonstrated rather than assumed:
//
//   importing `@wix/ai-scheduling-assistant-common-lib/services` from a bundled module pulls the WHOLE
//   services barrel into the client bundle, and that barrel reaches `AspectsManager` → `@wix/wix-aspects`,
//   which is Node-only. The dev server fails outright with `Module not found` on a transitive dep of a
//   function this page never calls.
//
// So: server code imports common-lib, browser code copies. Keep the two in step BY HAND — the values below
// are the ones a save is validated against server-side, so a copy that drifts does not produce a wrong
// answer, it produces a UI that offers something the endpoint then refuses.

/** The size cap on inline content, mirroring `MAX_WRITABLE_CONTENT_CHARS` (the file-system proto's
 *  `maxLength`). A longer body is refused server-side. */
export const MAX_WRITABLE_CONTENT_CHARS = 100_000;

/** Mirrors `EDITABLE_EXTENSIONS`. The text-shaped extensions — the ones stored inline, and therefore the
 *  ones an editor can hand back as a string. */
const EDITABLE_EXTENSIONS = ['md', 'markdown', 'txt', 'text', 'log', 'json', 'yaml', 'yml', 'csv', 'tsv'];

/** Mirrors `CREATABLE_FILE_TYPES`. The three types the New file dialog offers; the order IS the order the
 *  picker draws them in. */
export const CREATABLE_FILE_TYPES = [
  { type: 'markdown', extension: 'md' },
  { type: 'text', extension: 'txt' },
  { type: 'csv', extension: 'csv' },
] as const;

export type CreatableFileType = (typeof CREATABLE_FILE_TYPES)[number]['type'];

/** The lowercased extension of a file name, or `''` when it has none. */
export function fileExtension(name: string): string {
  const trimmed = name.trim();
  const dot = trimmed.lastIndexOf('.');
  if (dot <= 0 || dot === trimmed.length - 1) return '';
  return trimmed.slice(dot + 1).toLowerCase();
}

/** Whether this file may be opened in the editor — the rule behind the pencil.
 *
 *  Name only, on purpose: a row asks this before any body is loaded, and an affordance that appeared only
 *  after a fetch would flicker. A name that says `.md` but turns out to be reference-backed is caught on the
 *  way IN to the editor, where the content is known, and again by the endpoint. */
export function isEditableFileName(name: string): boolean {
  return EDITABLE_EXTENSIONS.includes(fileExtension(name));
}

/** The file name the New file dialog produces. Mirrors `createdFileName`: the picked type's extension is
 *  appended unless the name already ends in the extension THAT TYPE writes — so `notes` + Markdown gives
 *  `notes.md` and `notes.md` + Markdown stays `notes.md`, while `notes.txt` + Markdown becomes
 *  `notes.txt.md`, because the picker answers "what is this file", not the name's trailing characters. */
export function createdFileName(rawName: string, type: CreatableFileType): string {
  const name = rawName.trim();
  const entry = CREATABLE_FILE_TYPES.find((candidate) => candidate.type === type) ?? CREATABLE_FILE_TYPES[0];
  if (!name) return '';
  return fileExtension(name) === entry.extension ? name : `${name}.${entry.extension}`;
}

/** The part of a stored file the editor opens — the content with any legacy `<!--sym-file:…-->` metadata
 *  header stripped. Mirrors `fileEditableBody`; a no-op for anything written since the pointer-fields
 *  migration, which stores that metadata in real fields instead. */
export function fileEditableBody(content: string | undefined | null): string {
  if (!content) return '';
  return content.replace(/^<!--sym-file:.+?-->\n?/, '');
}

/** How an uploaded file should be stored. Mirrors `fileUploadStrategy`.
 *
 *  `inline` means the browser reads the text and writes it as content — no media round trip, and the file
 *  lands EDITABLE, because the pencil needs an inline body. `reference` is everything else: the bytes go
 *  to WixMP and the file points at them. Size is part of the rule, not just the extension: a text file
 *  too big to sit inline has to become a reference, and is then read-only like any other upload. */
export function fileUploadStrategy(name: string, sizeBytes: number): 'inline' | 'reference' {
  return isEditableFileName(name) && sizeBytes <= MAX_WRITABLE_CONTENT_CHARS ? 'inline' : 'reference';
}

/** A file name that does not collide with `taken` — `report.pdf` → `report (1).pdf`. Mirrors
 *  `uniqueFileName`. Dropping a folder onto the browser hits an existing name sooner or later, and the
 *  batch failing over it is not an acceptable answer; the server still refuses a real collision. */
export function uniqueFileName(name: string, taken: Iterable<string>): string {
  const existing = new Set([...taken].map((entry) => entry.toLowerCase()));
  if (!existing.has(name.toLowerCase())) return name;
  const extension = fileExtension(name);
  const stem = extension ? name.slice(0, name.length - extension.length - 1) : name;
  for (let n = 1; ; n += 1) {
    const candidate = extension ? `${stem} (${n}).${extension}` : `${stem} (${n})`;
    if (!existing.has(candidate.toLowerCase())) return candidate;
  }
}
